import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Observer } from "gsap/Observer";

gsap.registerPlugin(ScrollTrigger, Observer);

export default class Base {
    protected pageId: string;
    protected pageElement: HTMLElement | null;
    private static isGlobalInitialized = false;

    constructor(pageId: string = '') {
        this.pageId = pageId;
        this.pageElement = null;
        
        if (!Base.isGlobalInitialized) {
            this.initHeaderScroll();
            this.initMenu();
            Base.isGlobalInitialized = true;
        }
        
        if (this.pageId) {
            this.init();
        }
    }

    /**
     * Initialize the page
     * Override this method in child classes
     */
    init(): boolean {
        // Check if page element exists
        this.pageElement = document.getElementById(this.pageId);

        if (!this.pageElement) {
            // Page element not found, skipping initialization for this page
            return false;
        }

        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.initializeComponents();
            });
        } else {
            this.initializeComponents();
        }

        return true;
    }

    /**
     * Initialize page-specific components
     * Override this method in child classes
     */
    initializeComponents(): void {
        // To be implemented by child classes
    }

    initMenu() {
        const header = document.querySelector('.app-header');
        const menuToggle = document.querySelector('.header-menu-toggle');
        const menuOverlay = document.querySelector('.menu-overlay');

        if (menuToggle && menuOverlay && header) {
            const toggleMenu = (show: boolean) => {
                if (show) {
                    menuOverlay.classList.add('active');
                    menuToggle.classList.add('active');
                    header.classList.add('menu-open');
                    document.body.style.overflow = 'hidden'; // Prevent scrolling
                } else {
                    menuOverlay.classList.remove('active');
                    menuToggle.classList.remove('active');
                    header.classList.remove('menu-open');
                    document.body.style.overflow = '';
                }
            };
            
            // Add click listener not just to the button but specifically to the button that exists
            menuToggle.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation(); // Stop propagation to prevent immediate closing
                const isActive = menuOverlay.classList.contains('active');
                toggleMenu(!isActive);
            });
        }
    }

    initHeaderScroll() {
        const header = document.querySelector('.app-header');
        if (!header) return;

        ScrollTrigger.create({
            start: 'top top',
            end: 99999,
            onUpdate: (self) => {
                const scrollY = self.scroll();
                const direction = self.direction; // 1 = down, -1 = up
                
                // Calculate bottom using native measurements
                const scrollHeight = document.documentElement.scrollHeight;
                const windowHeight = window.innerHeight;
                const isBottom = (scrollY + windowHeight) >= (scrollHeight - 100);

                // Show if scrolling up OR near top OR near bottom
                if (direction === -1 || scrollY < 50 || isBottom) {
                     gsap.to(header, {
                        yPercent: 0,
                        autoAlpha: 1,
                        duration: 0.1,
                        ease: "none",
                        overwrite: true
                    });
                } 
                // Hide if scrolling down AND not at top AND not at bottom
                else if (direction === 1 && scrollY > 50 && !isBottom) {
                     gsap.to(header, {
                        yPercent: -100,
                        autoAlpha: 0,
                        duration: 0.1,
                        ease: "none",
                        overwrite: true
                    });
                }
            }
        });
    }
}
